// @flow
import React, { useContext } from 'react';
import { getIn, useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { isExpandedInputRequiredForKafka } from '@src/common-utils/utils';
import { useEstimateRateCardId } from '@src/custom-hooks/useEstimateRateCardId';
import { useTableflowVersion } from '@src/custom-hooks/useTableflowVersion';

import {
  AWS,
  CLUSTER_KAFKA_FORM,
  CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_PROVIDER_BACKEND_NAME,
  CLUSTER_TYPE_BACKEND_NAME,
  DEDICATED_CLUSTER_TYPE,
  ENTERPRISE_CLUSTER_TYPE,
  TABLEFLOW_VERSIONS,
} from '../../../constants';
import { Tooltip } from '../../presentational/Tooltip';
import { ClusterInputTable } from '../cluster-input-table/ClusterInputTable';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { toastError } from '../../presentational/notifications/utils';
import { useUpdateClusterMutation } from '../../../service-definitions/estimates';
import { EstimateContext } from '../../../contexts/EstimateContext';
import {
  getDataForOtherTabsThatNeedToBeSentToBackEnd,
  getKafkaNewlyDisabledMonths,
} from '../cluster-input-table/utils';
import { isAnythingEditedOnClusterPageBesidesTheGivenForm } from '../../views/utils';

const DONOT_INCLUDE_CL_THROUGHPUT_TOOLTIP_MESSAGE = 'Do not include cluster linking throughput';
const DONOT_INCLUDE_TF_AND_CL_THROUGHPUT_TOOLTIP_MESSAGE = 'Do not include TF and CL throughput';
const DONOT_INCLUDE_TF_THROUGHPUT_TOOLTIP_MESSAGE = 'Do not include Tableflow throughput';

// Only used for the non-Enterprise read rows.
// Legacy: read inputs include Tableflow, so the label stays plain.
// V2: read inputs exclude Tableflow, so the label gets the "do not include" tooltip.
const getReadThroughputDisplayName = (label, isLegacyTableflow) => {
  if (isLegacyTableflow) {
    return label;
  }
  return (
    <>
      {label}
      <Tooltip text={DONOT_INCLUDE_TF_THROUGHPUT_TOOLTIP_MESSAGE} />
    </>
  );
};

export const getOtherTabsInputsBasedOnWhetherMonthsAreDisabledOrNot = (
  kafkaMonthsData,
  initialValues,
  values,
  tableflowVersion
) => {
  let toRet = {};
  const disabledMonths = getKafkaNewlyDisabledMonths(kafkaMonthsData, initialValues, values);
  if (disabledMonths.length > 0) {
    toRet = getDataForOtherTabsThatNeedToBeSentToBackEnd(values, disabledMonths, tableflowVersion);
  }
  return toRet;
};

const getIfGivenClusterIsEnterprise = (values) => {
  return values[CLUSTER_TYPE_BACKEND_NAME] === ENTERPRISE_CLUSTER_TYPE;
};

const getIfGivenProviderIsAWS = (values) => {
  return values[CLUSTER_PROVIDER_BACKEND_NAME] === AWS;
};

export const KafkaUsageContainer = () => {
  const { estimateId, clusterId } = useParams();
  const [updateCluster] = useUpdateClusterMutation();
  const estimate = useContext(EstimateContext);
  const rateCardId = useEstimateRateCardId();
  // Disabling a Kafka month rewrites the Tableflow block, which is version-specific.
  const tableflowVersion = useTableflowVersion();
  const isLegacyTableflow = tableflowVersion === TABLEFLOW_VERSIONS.LEGACY;

  const { values, initialValues } = useFormikContext();

  const rowsConfig = [
    {
      backendName: 'is_enabled',
      displayName: 'Active',
      isBooleanField: true,
    },
    {
      backendName: 'write_throughput_peak_mbps',
      displayName: 'Peak Write Throughput (MBps)',
      showOrHideFunc: (values) => values[CLUSTER_TYPE_BACKEND_NAME] === DEDICATED_CLUSTER_TYPE,
    },
    {
      backendName: 'read_throughput_peak_mbps',
      displayName: getReadThroughputDisplayName('Peak Read Throughput (MBps)', isLegacyTableflow),
      displayNameForDownloads: 'Peak Read Throughput (MBps)',
      showOrHideFunc: (values) => values[CLUSTER_TYPE_BACKEND_NAME] === DEDICATED_CLUSTER_TYPE,
    },
    {
      backendName: 'write_throughput_avg_mbps_pl',
      displayName: (
        <>
          Avg Write Throughput (MBps) - Private Link
          <Tooltip text={DONOT_INCLUDE_CL_THROUGHPUT_TOOLTIP_MESSAGE} />
        </>
      ),
      displayNameForDownloads: 'Avg Write Throughput (MBps) - Private Link',
      showOrHideFunc: (values) => getIfGivenClusterIsEnterprise(values),
      // PL is applicable for all Providers
    },
    {
      backendName: 'write_throughput_avg_mbps_pni',
      displayName: (
        <>
          Avg Write Throughput (MBps) - PNI
          <Tooltip text={DONOT_INCLUDE_CL_THROUGHPUT_TOOLTIP_MESSAGE} />
        </>
      ),
      displayNameForDownloads: 'Avg Write Throughput (MBps) - PNI',
      showOrHideFunc: (values) =>
        getIfGivenClusterIsEnterprise(values) && getIfGivenProviderIsAWS(values),
      // PNI should only be shown for AWS
    },
    {
      backendName: 'read_throughput_avg_mbps_pl',
      displayName: (
        <>
          Avg Read Throughput (MBps) - Private Link
          <Tooltip text={DONOT_INCLUDE_TF_AND_CL_THROUGHPUT_TOOLTIP_MESSAGE} />
        </>
      ),
      displayNameForDownloads: 'Avg Read Throughput (MBps) - Private Link',
      showOrHideFunc: (values) => getIfGivenClusterIsEnterprise(values),
    },
    {
      backendName: 'read_throughput_avg_mbps_pni',
      displayName: (
        <>
          Avg Read Throughput (MBps) - PNI
          <Tooltip text={DONOT_INCLUDE_TF_AND_CL_THROUGHPUT_TOOLTIP_MESSAGE} />
        </>
      ),
      displayNameForDownloads: 'Avg Read Throughput (MBps) - PNI',
      showOrHideFunc: (values) =>
        getIfGivenClusterIsEnterprise(values) && getIfGivenProviderIsAWS(values),
    },
    {
      backendName: 'write_throughput_avg_mbps',
      displayName: 'Average Write Throughput (MBps)',
      showOrHideFunc: (values) => !getIfGivenClusterIsEnterprise(values),
      // Generic Read/Write throughput are not applicable for Enterprise
    },
    {
      backendName: 'read_throughput_avg_mbps',
      displayName: getReadThroughputDisplayName(
        'Average Read Throughput (MBps)',
        isLegacyTableflow
      ),
      displayNameForDownloads: 'Average Read Throughput (MBps)',
      showOrHideFunc: (values) => !getIfGivenClusterIsEnterprise(values),
    },
    {
      backendName: 'connected_client_count',
      displayName: '# of Simultaneously Connected Clients',
      showOrHideFunc: (values) =>
        isExpandedInputRequiredForKafka(values[CLUSTER_TYPE_BACKEND_NAME], rateCardId),
    },
    {
      backendName: 'client_connection_attempts_per_sec',
      displayName: '# of Connection Attempts Per Second',
      showOrHideFunc: (values) =>
        isExpandedInputRequiredForKafka(values[CLUSTER_TYPE_BACKEND_NAME], rateCardId),
    },
    {
      backendName: 'client_requests_per_sec',
      displayName: '# of Requests Per Second',
      showOrHideFunc: (values) =>
        isExpandedInputRequiredForKafka(values[CLUSTER_TYPE_BACKEND_NAME], rateCardId),
    },
  ];

  const shouldBeDisabled = isAnythingEditedOnClusterPageBesidesTheGivenForm(
    values,
    initialValues,
    CLUSTER_KAFKA_FORM
  );

  return (
    <PaddedAndRaisedSegment disabled={shouldBeDisabled}>
      <ClusterInputTable
        disabled={shouldBeDisabled}
        keyNameInValues={CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG}
        rowsConfig={rowsConfig}
        tableName="Kafka Monthly Usage"
        updateFunc={async () => {
          const valuesToSendToBackend = getIn(values, CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG);
          const kafkaInputs = Object.values(valuesToSendToBackend.months);
          const otherComponentsToSendToBackEnd =
            getOtherTabsInputsBasedOnWhetherMonthsAreDisabledOrNot(
              valuesToSendToBackend.months,
              initialValues,
              values,
              tableflowVersion
            );

          const payload = {
            id: clusterId,
            estimate_id: estimateId,
            estimate_version: estimate?.inputs?.estimate_version,
            throughput: { inputs: kafkaInputs },
            ...otherComponentsToSendToBackEnd,
          };

          const { error } = await updateCluster({
            estimateId,
            clusterId,
            payload,
          });

          if (error) {
            toastError(error);
          }
        }}
      />
    </PaddedAndRaisedSegment>
  );
};

// todo:: Fix the duplication in all 4 update funcs
